import React, { useEffect, useState } from "react";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCirclePlus, IconPencil, IconTrash } from "@tabler/icons";
import _ from "lodash";
import { DATA_CURRENCY } from "../data/options";
import { AR_FORM_NUMBER_INPUT_PROPS } from "../helpers/arFormUtils";
import { DATE_INPUT_FORMAT } from "../helpers/dateInputFormat";
import { useServerApi } from "../hooks/userServerApi";
import {
  buildExternalRateMap,
  emptyIfastAccountMovement,
  emptyWithdrawalLine,
  ensureWithdrawalDraftLines,
  IFAST_ACCOUNT_MOVEMENT_OPTIONS,
  ifastAccountMovementLabelParts,
  missingWithdrawalCurrencies,
  sanitizeIfastAccountMovement,
  showsMovementValueFields,
  showsNetContribution,
  sumWithdrawalAmountHkd,
  withdrawalAmountInHkd,
} from "../helpers/ifastAccountMovement";

const toDateValue = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-HK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "—";

const totalValueStyle = {
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: 36,
  paddingLeft: 12,
  paddingRight: 12,
  border: "1px solid transparent",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "34px",
};

const hkdValueStyle = {
  ...totalValueStyle,
  justifyContent: "flex-end",
  paddingLeft: 0,
  paddingRight: 0,
  border: "none",
  textAlign: "right",
};

const currencyChoices = (code) => {
  if (!code || DATA_CURRENCY.some((item) => item.value === code)) {
    return DATA_CURRENCY;
  }
  return [{ value: code, label: code }, ...DATA_CURRENCY];
};

const lineHasForeignAmount = (lines) =>
  (lines || []).some((line) => {
    if (!line?.currency || line.currency === "HKD") return false;
    const amount = line.withdrawalAmount ?? line.totalCurrentValue;
    return amount != null && amount !== "";
  });

const OrderIfastAccountMovements = ({ form, readOnly, disabled }) => {
  const locked = Boolean(readOnly || disabled);
  const rows = _.get(form?.values, "ifastAccountMovements") ?? [];
  const [api] = useServerApi();
  const [rateMap, setRateMap] = useState(null);
  const [rateError, setRateError] = useState("");
  const [opened, setOpened] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [draft, setDraft] = useState(emptyIfastAccountMovement());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .search({
        apiEntity: "exchangeRate",
        searchQuery: { active: true },
        pageSize: 1,
      })
      .then((result) => {
        if (cancelled) return;
        if (!result?.docs) {
          setRateError("Exchange rates could not be loaded.");
          setRateMap(null);
          return;
        }
        setRateMap(buildExternalRateMap(result.docs[0]));
      })
      .catch(() => {
        if (cancelled) return;
        setRateError("Exchange rates could not be loaded.");
        setRateMap(null);
      });
    return () => {
      cancelled = true;
    };
    // Load the active rate table once. The API object is a new identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepareDraft = (row) =>
    ensureWithdrawalDraftLines(
      sanitizeIfastAccountMovement(
        { ...row, date: toDateValue(row?.date) },
        rateMap || undefined
      )
    );

  const persistRows = async (nextRows) => {
    const sanitized = nextRows.map((row) =>
      sanitizeIfastAccountMovement(row, rateMap || undefined)
    );
    const previous = _.get(form?.values, "ifastAccountMovements") ?? [];
    form?.markUserEdited?.();
    form?.setFieldValue?.("ifastAccountMovements", sanitized);

    const orderId = form?.values?._id;
    if (!orderId || typeof form?.savePartialFields !== "function") {
      return true;
    }

    setSaving(true);
    try {
      const result = await form.savePartialFields(
        { ifastAccountMovements: sanitized },
        { quiet: true }
      );
      if (!result?.success) {
        form?.setFieldValue?.("ifastAccountMovements", previous);
        showNotification({
          color: "red",
          title: "Save Fail",
          message:
            result?.error?.message ||
            result?.message ||
            "Account movement was not saved.",
        });
        return false;
      }
      return true;
    } catch (error) {
      form?.setFieldValue?.("ifastAccountMovements", previous);
      showNotification({
        color: "red",
        title: "Save Fail",
        message: "Account movement was not saved.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditingIndex(-1);
    setDraft(emptyIfastAccountMovement());
    setErrors({});
    setOpened(true);
  };

  const openEdit = (index) => {
    const row = rows[index] ?? emptyIfastAccountMovement();
    setEditingIndex(index);
    setDraft(prepareDraft(row));
    setErrors({});
    setOpened(true);
  };

  const updateDraft = (patch) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "type")) {
        return ensureWithdrawalDraftLines(
          sanitizeIfastAccountMovement(next, rateMap || undefined)
        );
      }
      return next;
    });
    setErrors({});
  };

  const updateLine = (index, patch) => {
    setDraft((current) => {
      const lines = [...(current.withdrawalLines || [])];
      lines[index] = { ...(lines[index] || emptyWithdrawalLine()), ...patch };
      return { ...current, withdrawalLines: lines };
    });
    setErrors({});
  };

  const draftLines =
    draft.type === "CASH_WITHDRAWAL" ? draft.withdrawalLines || [] : [];
  const missingCurrencies = rateMap
    ? missingWithdrawalCurrencies(draftLines, rateMap)
    : [];
  const waitingForRates =
    rateMap == null && (Boolean(rateError) || lineHasForeignAmount(draftLines));
  const rateProblem = !showsMovementValueFields(draft.type)
    ? ""
    : missingCurrencies.length
      ? `No external exchange rate for ${missingCurrencies.join(", ")}`
      : waitingForRates
        ? rateError || "Exchange rates are still loading."
        : "";
  const withdrawalTotal = rateMap
    ? sumWithdrawalAmountHkd(draftLines, rateMap).total
    : null;

  const saveDraft = async () => {
    const nextErrors = {};
    if (!draft.date) nextErrors.date = "Date is required";
    if (!draft.type) nextErrors.type = "Type is required";
    if (rateProblem) nextErrors.rates = rateProblem;
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const sanitized = sanitizeIfastAccountMovement(draft, rateMap ?? { HKD: 1 });
    const nextRows = [...rows];
    if (editingIndex >= 0) nextRows[editingIndex] = sanitized;
    else nextRows.push(sanitized);
    const saved = await persistRows(nextRows);
    if (saved) setOpened(false);
  };

  const showValues = showsMovementValueFields(draft.type);

  return (
    <>
      {rows.length === 0 ? (
        <Text size="xs" color="dimmed" mb="xs">
          No account movements yet.
        </Text>
      ) : (
        <>
        <Text size="xs" color="dimmed" mb={6}>
          Amounts are in HKD.
        </Text>
        <div style={{ overflowX: "auto" }}>
        <Table fontSize="xs" verticalSpacing="xs" highlightOnHover style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Date</th>
              <th style={{ minWidth: 168 }}>Type</th>
              <th>Withdrawal Amount</th>
              <th>Total Current Value</th>
              <th>Total Profit / Loss</th>
              <th>Net Contribution</th>
              {!locked && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const typeLabel = ifastAccountMovementLabelParts(row.type);
              return (
              <tr key={row._id ?? index}>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.date)}</td>
                <td style={{ minWidth: 168 }}>
                  {typeLabel.en ? (
                    <>
                      <div style={{ whiteSpace: "nowrap" }}>{typeLabel.en}</div>
                      {typeLabel.zh ? (
                        <div style={{ whiteSpace: "nowrap" }}>{typeLabel.zh}</div>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {showsMovementValueFields(row.type)
                    ? formatAmount(
                        rateMap
                          ? sumWithdrawalAmountHkd(row.withdrawalLines, rateMap).total
                          : null
                      )
                    : "—"}
                </td>
                <td>
                  {showsMovementValueFields(row.type)
                    ? formatAmount(row.totalCurrentValue)
                    : "—"}
                </td>
                <td>
                  {showsMovementValueFields(row.type)
                    ? formatAmount(row.totalProfitLoss)
                    : "—"}
                </td>
                <td>
                  {showsNetContribution(row.type)
                    ? formatAmount(row.netContribution)
                    : "—"}
                </td>
                {!locked && (
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <Group spacing={4} noWrap position="right">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        aria-label={`Edit movement ${index + 1}`}
                        onClick={() => openEdit(index)}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        aria-label={`Remove movement ${index + 1}`}
                        onClick={() =>
                          persistRows(
                            rows.filter((_, rowIndex) => rowIndex !== index)
                          )
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </Table>
        </div>
        </>
      )}

      {!locked && (
        <Button
          size="xs"
          variant="default"
          leftIcon={<IconCirclePlus size={14} />}
          onClick={openAdd}
          mt="sm"
        >
          Add movement
        </Button>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={editingIndex >= 0 ? "Edit movement" : "Account Movement"}
        size={920}
      >
        <Stack>
          <DatePicker
            label="Date 日期"
            placeholder={DATE_INPUT_FORMAT}
            inputFormat={DATE_INPUT_FORMAT}
            value={toDateValue(draft.date)}
            onChange={(value) => updateDraft({ date: value })}
            error={errors.date}
            required
            clearable
            zIndex={1000}
          />
          <Select
            label="Type 類型"
            placeholder="Select movement type"
            data={IFAST_ACCOUNT_MOVEMENT_OPTIONS}
            value={draft.type || null}
            onChange={(value) => updateDraft({ type: value ?? "" })}
            error={errors.type}
            required
            searchable
            clearable
          />
          {showValues && (
            <>
            <Paper withBorder p="sm" radius="sm">
              <div style={{ overflowX: "auto" }}>
                <Table fontSize="xs" verticalSpacing="xs" style={{ minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 140 }}>Currency 貨幣</th>
                      <th style={{ minWidth: 200 }}>Withdrawal Amount 提取金額</th>
                      <th style={{ minWidth: 180, textAlign: "right" }}>In HKD 港幣</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {draftLines.map((line, index) => (
                      <tr key={`${line.currency}-${index}`}>
                        <td>
                          <Select
                            aria-label={`Currency ${index + 1}`}
                            data={currencyChoices(line.currency)}
                            value={line.currency || "HKD"}
                            onChange={(value) =>
                              updateLine(index, { currency: value || "HKD" })
                            }
                            searchable
                          />
                        </td>
                        <td>
                          <NumberInput
                            aria-label={`Withdrawal Amount ${index + 1}`}
                            value={line.withdrawalAmount ?? ""}
                            onChange={(value) =>
                              updateLine(index, { withdrawalAmount: value })
                            }
                            precision={2}
                            hideControls
                            {...AR_FORM_NUMBER_INPUT_PROPS}
                          />
                        </td>
                        <td>
                          <div style={{ ...hkdValueStyle, fontWeight: 400 }}>
                            {formatAmount(
                              rateMap ? withdrawalAmountInHkd(line, rateMap) : null
                            )}
                          </div>
                        </td>
                        <td>
                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            aria-label={`Remove currency ${index + 1}`}
                            onClick={() =>
                              setDraft((current) => {
                                const next = (current.withdrawalLines || []).filter(
                                  (_, lineIndex) => lineIndex !== index
                                );
                                return {
                                  ...current,
                                  withdrawalLines: next.length
                                    ? next
                                    : [emptyWithdrawalLine()],
                                };
                              })
                            }
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f8f9fa" }}>
                      <td>
                        <div style={totalValueStyle}>Total (HKD)</div>
                      </td>
                      <td />
                      <td style={{ textAlign: "right" }}>
                        <div style={hkdValueStyle}>
                          {formatAmount(withdrawalTotal)}
                        </div>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </Table>
              </div>
              {rateProblem ? (
                <Text size="sm" color="red" mt="xs">
                  {errors.rates || rateProblem}
                </Text>
              ) : null}
              <Button
                size="xs"
                mt="sm"
                style={{ width: "fit-content" }}
                onClick={() =>
                  setDraft((current) => ({
                    ...current,
                    withdrawalLines: [
                      ...(current.withdrawalLines || []),
                      emptyWithdrawalLine(),
                    ],
                  }))
                }
              >
                Add
              </Button>
            </Paper>
            <Paper withBorder p="sm" radius="sm">
              <Text size="xs" weight={600} mb="sm">
                iFast Master Log
              </Text>
              <Group grow align="flex-end">
                <NumberInput
                  label="Total Current Value (HKD) 總現值"
                  value={draft.totalCurrentValue ?? ""}
                  onChange={(value) => updateDraft({ totalCurrentValue: value })}
                  precision={2}
                  hideControls
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
                <NumberInput
                  label="Total Profit / Loss (HKD) 總盈虧"
                  value={draft.totalProfitLoss ?? ""}
                  onChange={(value) => updateDraft({ totalProfitLoss: value })}
                  precision={2}
                  hideControls
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
                <NumberInput
                  label="Net Contribution (HKD) 淨供款"
                  value={draft.netContribution ?? ""}
                  onChange={(value) => updateDraft({ netContribution: value })}
                  precision={2}
                  hideControls
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Group>
            </Paper>
            </>
          )}
          <Group position="right">
            <Button
              variant="default"
              onClick={() => setOpened(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={saveDraft} loading={saving} disabled={Boolean(rateProblem)}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default OrderIfastAccountMovements;
